import * as Sentry from '@sentry/browser';
import API from '@youga/youga-client-api';
import type {
  LiveSession,
  SplitChallengeScheduleItem,
} from '@youga/youga-interfaces';
import axios from 'axios';

import env from '../environment';

/* @deprecated */
const WebappAPI = API(env.GATSBY_API_URL, Sentry);

export interface AdminContentStatus {
  assets: Array<{
    id: string;
    key: string;
    required: boolean;
    type: 'image' | 'video';
    exists: boolean;
  }>;
  processed: Record<string, boolean>;
  published: Record<string, boolean>;
}

export interface AdminAccessProfile {
  email: string;
  role: 'superadmin' | 'admin';
  active: boolean;
  permissions: Record<string, 'read' | 'write'>;
}
export interface AdministratorRow {
  id: string;
  email: string;
  status: string;
  enabled: boolean;
  access: AdminAccessProfile | null;
}
export type AdminLiveSession = Omit<LiveSession, 'providerJoinUrl'> & {
  meetingConfigured?: boolean;
};
export interface LiveCreditAudit {
  userId: string;
  email: string;
  balance: number;
  entries: Array<{
    entryId: string;
    delta: number;
    type: string;
    source: string;
    reason?: string;
    createdAt: string;
  }>;
}
export interface AdminSplitChallenge {
  enabled: boolean;
  rows: SplitChallengeScheduleItem[];
}

const internalPut = async <T>(
  idToken: string,
  path: string,
  body: unknown = {},
): Promise<T> => {
  const response = await axios.put<T>(
    `${env.GATSBY_API_URL}/businessInternal/${path}`,
    body,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
  return response.data;
};

const AdminAPI = {
  ...WebappAPI,
  getAdminContentStatus: async (
    idToken: string,
    body: {
      type: 'course' | 'session';
      id: string;
      instructor?: string;
    },
  ): Promise<AdminContentStatus> => {
    const response = await axios.put<AdminContentStatus>(
      `${env.GATSBY_API_URL}/businessInternal/content-status`,
      body,
      { headers: { Authorization: `Bearer ${idToken}` } },
    );
    return response.data;
  },
  getAdminProfile: (token: string) =>
    internalPut<AdminAccessProfile>(token, 'admin-profile'),
  getAdministrators: (token: string) =>
    internalPut<AdministratorRow[]>(token, 'admin-list'),
  createAdministrator: (token: string, body: unknown) =>
    internalPut(token, 'admin-create', body),
  updateAdministrator: (token: string, body: unknown) =>
    internalPut(token, 'admin-update', body),
  deleteAdministrator: (token: string, id: string) =>
    internalPut(token, 'admin-delete', { id }),
  getFinance: (token: string, from: string, to: string) =>
    internalPut<{ rows: any[] }>(token, 'finance', { from, to }),
  getLiveSessions: async (token: string): Promise<AdminLiveSession[]> =>
    (await internalPut<{ rows: AdminLiveSession[] }>(token, 'live-list')).rows,
  putLiveSession: (token: string, body: unknown) =>
    internalPut<{ ok: true; data: AdminLiveSession }>(token, 'live-put', body),
  cancelLiveSession: (token: string, id: string) =>
    internalPut<{ ok: true }>(token, 'live-cancel', { id }),
  getLiveCoverUploadUrl: (
    token: string,
    body: { id: string; contentType: string; fileSize: number },
  ) =>
    internalPut<{ url: string; key: string; expiresIn: number }>(
      token,
      'live-cover-upload',
      body,
    ),
  getLiveCreditAudit: (token: string, email: string) =>
    internalPut<LiveCreditAudit>(token, 'live-credit-audit', { email }),
  adjustLiveCredits: (
    token: string,
    body: { email: string; amount: number; reason: string },
  ) =>
    internalPut<{
      ok: true;
      email: string;
      requestedAmount: number;
      appliedAmount: number;
      balance: number;
    }>(token, 'live-credit-adjust', body),
  getSplitChallenge: (token: string) =>
    internalPut<AdminSplitChallenge>(token, 'split-challenge-list'),
  putSplitChallengeDay: (token: string, body: unknown) =>
    internalPut(token, 'split-challenge-put', body),
  deleteSplitChallengeDay: (token: string, date: string) =>
    internalPut(token, 'split-challenge-delete', { date }),
  setSplitChallengeFeature: (token: string, enabled: boolean) =>
    internalPut(token, 'split-challenge-feature', { enabled }),
  uploadLiveCover: async (uploadUrl: string, file: File): Promise<void> => {
    await axios.put(uploadUrl, file, {
      headers: { 'Content-Type': file.type },
    });
  },
};

export default AdminAPI;
